/*
  SmartRecommendations — Nosto-style intelligent product recommendations
  -----------------------------------------------------------------------
  Sources:
    1. Complementary products  (Shopify recommendations API, intent=complementary) — score 300
    2. Related products        (Shopify recommendations API, intent=related)        — score 200
    3. Recently viewed         (localStorage, recency-weighted)                     — score 250–8 per slot

  Cross-source bonus:
    +150  appears in 2 sources
    +300  appears in 3 sources

  Session behavioral signals (sessionStorage — resets on browser close):
    +up to 100  vendor affinity (same brand as previously viewed products)
    +up to  80  type affinity   (same type as previously viewed products)
    +180        click signal    (product clicked in this session's recommendations)

  Flow:
    1. Fetch sources 1 & 2 as JSON in parallel; read source 3 from localStorage
    2. Build a scored Map<productId, {score, sources}>
    3. Sort IDs by score desc, slice to display limit
    4. Fetch /search?section_id=…&type=product&q=id:A OR id:B OR id:C
       (Section renders cards in Liquid in the same order via parsed_terms trick)
    5. Inject inner HTML into the custom element
*/

class SmartRecommendations extends HTMLElement { 
  constructor() {
    super();

    // In "section rendering" mode the element has Liquid-rendered content,
    // data-product-id is absent — nothing to do.
    if (!this.dataset.productId) return;

    // Require localStorage so we can read recently-viewed IDs.
    if (!window.theme?.config?.hasLocalStorage) {
      this._init();
      return;
    }

    // Lazy-init when the section scrolls near the viewport.
    if (typeof Motion !== 'undefined' && Motion.inView) {
      Motion.inView(this, this._init.bind(this), { margin: '600px 0px 600px 0px' });
    } else {
      // Fallback: IntersectionObserver
      const io = new IntersectionObserver((entries, obs) => {
        if (entries[0].isIntersecting) {
          obs.disconnect();
          this._init();
        }
      }, { rootMargin: '600px 0px 600px 0px' });
      io.observe(this);
    }
  }

  /* ---------------------------------------------------------------
     Public init – can also be called manually
  --------------------------------------------------------------- */
  _init() {
    if (this._initialized) return;
    this._initialized = true;

    this._updateSessionAffinity();
    const currentId = parseInt(this.dataset.productId, 10);

    Promise.allSettled([
      this._fetchRecommendations(this.dataset.relatedUrl),
      this._fetchRecommendations(this.dataset.complementaryUrl),
    ]).then(([relatedResult, complementaryResult]) => {
      const related       = relatedResult.status      === 'fulfilled' ? relatedResult.value      : [];
      const complementary = complementaryResult.status === 'fulfilled' ? complementaryResult.value : [];
      const recentlyViewed = this._getRecentlyViewed(currentId);

      const sortedIds = this._scoreAndSort(related, complementary, recentlyViewed, currentId);

      if (sortedIds.length === 0) {
        this._hide();
        return;
      }

      const limit = parseInt(this.dataset.limit, 10) || 8;
      let idsToRender = sortedIds.slice(0, limit);
      // Guarantee recently-viewed items are visible: inject up to 2 that
      // didn't make the scored top-N, placing them just after the top result
      // so they appear prominently in the carousel without displacing #1.
      const inResults = new Set(idsToRender);
      const rvGuaranteed = recentlyViewed.filter(id => !inResults.has(id)).slice(0, 2);
      if (rvGuaranteed.length > 0) {
        idsToRender = [
          idsToRender[0],
          ...rvGuaranteed,
          ...idsToRender.slice(1, limit - rvGuaranteed.length),
        ];
      }
      this._renderSection(idsToRender);
    });
  }

  /* ---------------------------------------------------------------
     Fetch helpers
  --------------------------------------------------------------- */
  _fetchRecommendations(url) {
    if (!url) return Promise.resolve([]);
    return fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => (data.products || []).map(p => ({
      id:           p.id,
      vendor:       p.vendor       || '',
      product_type: p.product_type || '',
    })));
  }

  _getRecentlyViewed(excludeId) {
    try {
      const key = `${window.theme?.settings?.themeName || 'theme'}:recently-viewed`;
      const stored = localStorage.getItem(key) || '[]';
      return JSON.parse(stored).filter(id => id !== excludeId);
    } catch {
      return [];
    }
  }

  /* ---------------------------------------------------------------
     Session behavioral signals
     Key: 'smart-recs:session' in sessionStorage (auto-clears on browser close)
     Shape: { vendors: {name: count}, types: {name: count}, clicks: [id, ...] }
  --------------------------------------------------------------- */
  _updateSessionAffinity() {
    const vendor = this.dataset.productVendor;
    const type   = this.dataset.productType;
    if (!vendor && !type) return;
    try {
      const data    = JSON.parse(sessionStorage.getItem('smart-recs:session') || '{}');
      const vendors = data.vendors || {};
      const types   = data.types   || {};
      if (vendor) vendors[vendor] = (vendors[vendor] || 0) + 1;
      if (type)   types[type]     = (types[type]     || 0) + 1;
      sessionStorage.setItem('smart-recs:session', JSON.stringify({ vendors, types, clicks: data.clicks || [] }));
    } catch { /* storage unavailable */ }
  }

  _readSessionSignals() {
    try {
      const data = JSON.parse(sessionStorage.getItem('smart-recs:session') || '{}');
      return {
        vendors: data.vendors || {},
        types:   data.types   || {},
        clicks:  new Set(data.clicks || []),
      };
    } catch {
      return { vendors: {}, types: {}, clicks: new Set() };
    }
  }

  _trackClicks() {
    this.addEventListener('click', e => {
      const card = e.target.closest('[data-product-id]');
      if (!card) return;
      const id = parseInt(card.dataset.productId, 10);
      if (!id) return;
      try {
        const data   = JSON.parse(sessionStorage.getItem('smart-recs:session') || '{}');
        const clicks = data.clicks || [];
        if (!clicks.includes(id)) clicks.unshift(id);
        sessionStorage.setItem('smart-recs:session', JSON.stringify({ ...data, clicks: clicks.slice(0, 20) }));
      } catch { /* ignore */ }
    }, { passive: true });
  }

  /* ---------------------------------------------------------------
     Scoring engine
  --------------------------------------------------------------- */
  _scoreAndSort(related, complementary, recentlyViewed, excludeId) {
    // Map<productId: number, { score: number, sources: Set<string> }>
    const scores = new Map();

    const add = (id, points, source) => {
      if (id === excludeId || !id) return;
      if (!scores.has(id)) scores.set(id, { score: 0, sources: new Set() });
      const entry = scores.get(id);
      entry.score += points;
      entry.sources.add(source);
    };

    // 1. Complementary — highest commercial intent
    complementary.forEach(p => add(p.id, 300, 'complementary'));

    // 2. Related — editorial / content similarity
    related.forEach(p => add(p.id, 200, 'related'));

    // 3. Recently viewed — base 250 ensures recently-viewed-only products
    //    (250 pts) outrank related-only products (200 pts), so the section
    //    shows genuinely different products from product-recommendations.
    recentlyViewed.forEach((id, idx) => {
      const recencyBonus = Math.max(8, 250 - idx * 15); // 250, 235, 220 … floor at 8
      add(id, recencyBonus, 'recently_viewed');
    });

    // Cross-source bonus: reward products that appear in multiple sources
    scores.forEach(entry => {
      if (entry.sources.size === 2) entry.score += 150;
      if (entry.sources.size >= 3) entry.score += 300;
    });

    // Session behavioral signals: vendor/type affinity + click intent
    const session = this._readSessionSignals();
    [...complementary, ...related].forEach(p => {
      const entry = scores.get(p.id);
      if (!entry) return;
      if (p.vendor && session.vendors[p.vendor])
        entry.score += Math.min(session.vendors[p.vendor] * 40, 100);
      if (p.product_type && session.types[p.product_type])
        entry.score += Math.min(session.types[p.product_type] * 30, 80);
      if (session.clicks.has(p.id))
        entry.score += 180;
    });

    // Sort by score descending
    return Array.from(scores.keys()).sort(
      (a, b) => scores.get(b).score - scores.get(a).score
    );
  }

  /* ---------------------------------------------------------------
     Section rendering — fetch Liquid-rendered product cards
     The section uses parsed_terms iteration to honour the ID order.
  --------------------------------------------------------------- */
  _renderSection(sortedIds) {
    const query = sortedIds.map(id => `id:${id}`).join(' OR ');
    const url   = this.dataset.url + query;

    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(html => {
        const doc   = new DOMParser().parseFromString(html, 'text/html');
        const root  = doc.querySelector('.shopify-section');
        const inner = root?.querySelector('smart-recommendations');

        if (inner && inner.innerHTML.trim().length) {
          this.innerHTML = inner.innerHTML;

          // The nav buttons (PreviousButton/NextButton) call this.load() in their
          // constructor, but at that moment the slider-element is not yet in the DOM
          // (buttons are inserted before the slider in document order). This means
          // their slider:previousStatus/slider:nextStatus listeners are never
          // registered, leaving the "previous" button stuck as disabled.
          //
          // Double rAF ensures Motion.inView has already called slider.init()
          // (which dispatches the initial status events) before we re-bind.
          requestAnimationFrame(() => requestAnimationFrame(() => {
            this.querySelectorAll('button[aria-controls]').forEach(btn => {
              if (typeof btn.unload === 'function') btn.unload();
              if (typeof btn.load  === 'function') btn.load();
            });
            const slider = this.querySelector('slider-element');
            if (slider?.updateButtons) slider.updateButtons();
          }));

          this._trackClicks();
          this.dispatchEvent(new CustomEvent('smartRecommendations:loaded', { bubbles: true }));
        } else {
          this._hide();
        }
      })
      .catch(() => this._hide());
  }

  _hide() {
    this.closest('.smart-recs-section')?.remove();
  }
}

customElements.define('smart-recommendations', SmartRecommendations);
